import { useState, useEffect } from 'react'
import firebase from '../../firebase/compat'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { client } from '../../hooks/Client'
import useSettings from '../../hooks/Settings'
import SuperAdmin from '../../hooks/SuperAdmin'
import useSubscriptionPricing from '../../hooks/useSubscriptionPricing'
import MODULES from '../../helpers/subscriptionHelpers/moduleDefinitions'
import MODULE_DESCRIPTIONS from '../../helpers/subscriptionHelpers/moduleDescriptions'
import calculateTotal from '../../helpers/subscriptionHelpers/calculateTotal'
import { centsToInput, inputToPayload, toDateInputValue } from '../../helpers/subscriptionHelpers/invoiceAmountInput'
import Modal from '../common/Modal'

const MODULE_ORDER = ['impactMeten', 'personas', 'housevisits', 'portfolio']

const Subscription = ({ selectedOption }) => {

    const subscription = useSettings().subscription
    const { monthlyTotal: currentTotal, partner: currentPartner, status } = useSubscriptionPricing()
    const isSuperAdmin = SuperAdmin().admin

    const [draftModules, setDraftModules] = useState({})
    const [submitting, setSubmitting] = useState(false)
    const [managing, setManaging] = useState(false)
    const [error, setError] = useState('')
    const [infoSlug, setInfoSlug] = useState(null)
    const [contactModalOpen, setContactModalOpen] = useState(false)
    const [endDateInput, setEndDateInput] = useState('')
    const [savingEndDate, setSavingEndDate] = useState(false)
    const [invoiceAmountInput, setInvoiceAmountInput] = useState('')
    const [invoiceStartInput, setInvoiceStartInput] = useState('')
    const [savingInvoice, setSavingInvoice] = useState(false)
    // Eigen terugkoppeling naast de knop. De gedeelde `error` hieronder rendert
    // pas in het modulesblok, ver buiten beeld — een mislukte opslag zou daar
    // onzichtbaar blijven en dus als geslaagd lezen.
    const [invoiceMessage, setInvoiceMessage] = useState(/** @type {{type: 'ok'|'error', text: string} | null} */ (null))
    // Per veld bijhouden of de beheerder eraan gezeten heeft; zie het effect hieronder.
    const [invoiceTouched, setInvoiceTouched] = useState({ amount: false, start: false })

    useEffect(() => {
        if (!subscription?.modules) return
        // Free-included partner modules zijn altijd actief en moeten in
        // draftModules op true staan — anders stuurt handleApplyChanges ze als
        // false naar Stripe, en wist de webhook ze uit `Subscriptions.modules`.
        // Dat brak de sidebar voor FIX-klanten (Personas verdween).
        const freeIncluded = currentPartner?.freeIncludedModules || []
        const seeded = { ...subscription.modules }
        freeIncluded.forEach((slug) => { seeded[slug] = true })
        setDraftModules(seeded)
    }, [subscription, currentPartner])

    // Volgen wat er opgeslagen staat zolang de beheerder er niet aan gezeten
    // heeft; daarna nooit meer aanraken.
    //
    // Eén keer vullen was niet goed genoeg: `subscription` wordt gezet zodra
    // CompagnyMeta binnen is, wat vóór het Subscriptions-document kan zijn. Een
    // eenmalige vulling pakt dan de lege tussenstand en herstelt nooit — het
    // veld lijkt leeg terwijl er een bedrag staat, en 'Opslaan' zet het
    // vervolgens stil op null.
    //
    // Meelopen met élke snapshot kan ook niet: dat wist wat de beheerder aan het
    // typen is (pitfalls.md#state-draft-wiped-by-firestore-snapshot). De
    // aanraak-vlag scheidt die twee gevallen: nog niet aangeraakt betekent 'toon
    // wat er staat', aangeraakt betekent 'blijf eraf'.
    useEffect(() => {
        if (!subscription) return
        if (!invoiceTouched.amount) {
            setInvoiceAmountInput(centsToInput(subscription.InvoiceAmountCents))
        }
        if (!invoiceTouched.start) {
            setInvoiceStartInput(toDateInputValue(subscription.InvoicePeriodStart))
        }
    }, [subscription, invoiceTouched])

    const draftTotal = calculateTotal({ activeModules: draftModules, partner: currentPartner })

    const moduleChanged = MODULE_ORDER.some((slug) => Boolean(draftModules[slug]) !== Boolean(subscription?.modules?.[slug]))

    const hasStripeAccount = Boolean(subscription?.stripeCustomerId)

    const checkoutDisabled = !moduleChanged || submitting

    const activeModuleSlugs = MODULE_ORDER.filter((slug) => Boolean(subscription?.modules?.[slug]))

    const periodEnd = subscription?.currentPeriodEnd?.toDate
        ? subscription.currentPeriodEnd.toDate()
        : (subscription?.currentPeriodEnd instanceof Date ? subscription.currentPeriodEnd : null)

    const endDate = subscription?.endDate?.toDate
        ? subscription.endDate.toDate()
        : (subscription?.endDate instanceof Date ? subscription.endDate : null)

    // Einddatum rood markeren zodra hij vóór vandaag ligt (kalenderdag, niet klok).
    const startOfToday = new Date()
    startOfToday.setHours(0, 0, 0, 0)
    const endDateOverdue = endDate && endDate < startOfToday

    // Seed het date-input zodra de subscription (en dus de opgeslagen endDate) laadt.
    useEffect(() => {
        if (!endDate) { setEndDateInput(''); return }
        const yyyy = endDate.getFullYear()
        const mm = String(endDate.getMonth() + 1).padStart(2, '0')
        const dd = String(endDate.getDate()).padStart(2, '0')
        setEndDateInput(`${yyyy}-${mm}-${dd}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscription?.endDate])

    const handleToggle = (slug) => {
        setDraftModules((prev) => ({ ...prev, [slug]: !prev[slug] }))
    }

    const handleApplyChanges = async () => {
        // Factuurklanten kunnen modules niet zelf via Stripe wijzigen — verwijs
        // ze naar Deccos in plaats van een checkout te starten.
        if (!hasStripeAccount) {
            setContactModalOpen(true)
            return
        }
        setError('')
        setSubmitting(true)
        try {
            if (subscription?.stripeSubscriptionId) {
                // Bestaande sub → in-place update (behoudt subscription-level
                // discounts/coupons + voorkomt dubbele factuur). Webhook updates
                // Subscriptions.modules async; de Firestore-listener in deze
                // component picked het op zodra `customer.subscription.updated`
                // landt.
                const updateSubscription = firebase.functions().httpsCallable('updateSubscription')
                await updateSubscription({
                    client,
                    activeModules: draftModules,
                    partner: subscription?.partner || null,
                })
                setSubmitting(false)
                return
            }

            // Geen bestaande Stripe-sub → eerste checkout (maakt customer +
            // subscription op Stripe aan).
            const createCheckoutSession = firebase.functions().httpsCallable('createCheckoutSession')
            const result = await createCheckoutSession({
                client,
                activeModules: draftModules,
                partner: subscription?.partner || null,
                successUrl: `${window.location.origin}/${client}/Settings/7`,
                cancelUrl: `${window.location.origin}/${client}/Settings/7`,
            })
            window.location.href = result.data.url
        } catch (err) {
            console.error('handleApplyChanges failed:', err)
            setError(err.message || 'Onbekende fout bij wijzigen van abonnement.')
            setSubmitting(false)
        }
    }

    const handleManageSubscription = async () => {
        setError('')
        setManaging(true)
        try {
            const manageSubscription = firebase.functions().httpsCallable('manageSubscription')
            const result = await manageSubscription({
                client,
                returnUrl: `${window.location.origin}/${client}/Settings/7`,
            })
            window.location.href = result.data
        } catch (err) {
            console.error('manageSubscription failed:', err)
            setError(err.message || 'Kon Stripe-portaal niet openen.')
            setManaging(false)
        }
    }

    const handleAddPaymentMethod = async () => {
        setError('')
        setSubmitting(true)
        try {
            const createSetupSession = firebase.functions().httpsCallable('createSetupSession')
            const result = await createSetupSession({
                client,
                successUrl: `${window.location.origin}/${client}/Settings/7`,
                cancelUrl: `${window.location.origin}/${client}/Settings/7`,
            })
            window.location.href = result.data.url
        } catch (err) {
            console.error('createSetupSession failed:', err)
            setError(err.message || 'Onbekende fout bij toevoegen van betaalmethode.')
            setSubmitting(false)
        }
    }

    const handleSaveEndDate = async () => {
        setError('')
        setSavingEndDate(true)
        try {
            // v2-functie draait in europe-west1 — region expliciet meegeven,
            // anders roept de SDK us-central1 aan en faalt de call.
            const setSubscriptionEndDate = firebase.app().functions('europe-west1').httpsCallable('setSubscriptionEndDate')
            await setSubscriptionEndDate({ compagnyId: client, endDate: endDateInput || '' })
            // De Firestore-listener (useSettings) pikt de nieuwe endDate op.
        } catch (err) {
            console.error('setSubscriptionEndDate failed:', err)
            setError(err.message || 'Kon einddatum niet opslaan.')
        } finally {
            setSavingEndDate(false)
        }
    }

    const handleSaveInvoice = async () => {
        setInvoiceMessage(null)
        setSavingInvoice(true)
        try {
            // v2-functie in europe-west1 — region expliciet, anders belt de SDK
            // us-central1 en bestaat de functie daar niet.
            const setSubscriptionInvoice = firebase.app().functions('europe-west1').httpsCallable('setSubscriptionInvoice')
            await setSubscriptionInvoice({
                compagnyId: client,
                ...inputToPayload(invoiceAmountInput, invoiceStartInput),
            })
            // De Firestore-listener (useSettings) pikt de nieuwe waarden op.
            setInvoiceMessage({ type: 'ok', text: 'Opgeslagen.' })
        } catch (err) {
            console.error('setSubscriptionInvoice failed:', err)
            setInvoiceMessage({ type: 'error', text: err.message || 'Kon factuurbedrag niet opslaan.' })
        } finally {
            setSavingInvoice(false)
        }
    }

    if (selectedOption !== '7') return null

    return (
        <div className="settings-container">

            {!hasStripeAccount && (
                <div className="subscription-banner">
                    <p>Je betaalt nu per factuur. Voeg een betaalmethode toe om modules zelf te beheren.</p>
                    <button onClick={handleAddPaymentMethod} disabled={submitting}>
                        {submitting ? 'Even geduld…' : 'Voeg betaalmethode toe'}
                    </button>
                </div>
            )}

            {status === 'past_due' && (
                <div className="subscription-banner subscription-banner-warning">
                    <p>Je laatste betaling is mislukt. Werk je betaalgegevens bij om je abonnement actief te houden.</p>
                </div>
            )}

            <div className="profile-inner-container">
                <h2>Huidig abonnement</h2>

                <div className="subscription-plan-grid">
                    <div className="subscription-plan-row">
                        <span className="subscription-plan-label">Modules</span>
                        <div className="subscription-plan-chips">
                            {activeModuleSlugs.length > 0
                                ? activeModuleSlugs.map((slug) => (
                                    <span key={slug} className="subscription-plan-chip">
                                        {MODULES[slug]?.name || slug}
                                    </span>
                                ))
                                : <span className="subscription-plan-empty">Geen actieve modules</span>
                            }
                        </div>
                    </div>

                    <div className="subscription-plan-row">
                        <span className="subscription-plan-label">Maandprijs</span>
                        <span className="subscription-plan-value">€{currentTotal}/mnd</span>
                    </div>

                    {hasStripeAccount ? (
                        periodEnd && (
                            <div className="subscription-plan-row">
                                <span className="subscription-plan-label">Automatische incasso</span>
                                <span className="subscription-plan-value">
                                    Huidige periode loopt tot {periodEnd.toLocaleDateString('nl-NL')}
                                </span>
                            </div>
                        )
                    ) : (
                        <>
                            <div className="subscription-plan-row">
                                <span className="subscription-plan-label">Automatische incasso</span>
                                <span className="subscription-plan-value">Factuur</span>
                            </div>
                            <div className="subscription-plan-row">
                                <span className="subscription-plan-label">Periode</span>
                                <span className="subscription-plan-value">-</span>
                            </div>
                        </>
                    )}

                    {!hasStripeAccount && (endDate || isSuperAdmin) && (
                        <div className="subscription-plan-row">
                            <span className="subscription-plan-label">Einddatum</span>
                            {isSuperAdmin ? (
                                <div className="subscription-enddate-edit">
                                    <input
                                        type="date"
                                        value={endDateInput}
                                        onChange={(e) => setEndDateInput(e.target.value)}
                                    />
                                    <button onClick={handleSaveEndDate} disabled={savingEndDate}>
                                        {savingEndDate ? 'Even geduld…' : 'Opslaan'}
                                    </button>
                                    {endDate && (
                                        <span className={endDateOverdue ? 'subscription-plan-value-overdue' : 'subscription-plan-value'}>
                                            {endDate.toLocaleDateString('nl-NL')}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <span className={endDateOverdue ? 'subscription-plan-value-overdue' : 'subscription-plan-value'}>
                                    {endDate.toLocaleDateString('nl-NL')}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Factuurafspraak — alleen voor Deccos-superadmins.
                        Bewust NIET verbergen op `hasStripeAccount`: dat veld zegt
                        alleen dat er ooit een Stripe-klant is aangemaakt, niet dat
                        er een lopend abonnement is. Twee actieve omgevingen hebben
                        wel een klant-id en géén abonnement (o.a. FIXbrigade
                        Zutphen); die zouden dan nooit ingevuld kunnen worden en
                        eeuwig in de teller 'geen bedrag' blijven staan.
                        Of Stripe wint bepaalt de server — die vraagt het bij Stripe
                        na, wat de enige betrouwbare bron is (inbox #145).
                        Spec: docs/features/platform-mrr-card.md */}
                    {isSuperAdmin && (
                        <div className="subscription-plan-row">
                            <span className="subscription-plan-label">Factuurafspraak</span>
                            <div className="subscription-invoice-cell">
                            <div className="subscription-invoice-edit">
                                <label className="subscription-invoice-field">
                                    <span>Bedrag per periode (excl. btw)</span>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="1200,00"
                                        value={invoiceAmountInput}
                                        onChange={(e) => {
                                            setInvoiceTouched((t) => ({ ...t, amount: true }))
                                            setInvoiceAmountInput(e.target.value)
                                        }}
                                    />
                                </label>
                                <label className="subscription-invoice-field">
                                    <span>Periode begint</span>
                                    <input
                                        type="date"
                                        value={invoiceStartInput}
                                        onChange={(e) => {
                                            setInvoiceTouched((t) => ({ ...t, start: true }))
                                            setInvoiceStartInput(e.target.value)
                                        }}
                                    />
                                </label>
                                <button onClick={handleSaveInvoice} disabled={savingInvoice}>
                                    {savingInvoice ? 'Even geduld…' : 'Opslaan'}
                                </button>
                            </div>
                            {invoiceMessage && (
                                <p className={invoiceMessage.type === 'error'
                                    ? 'subscription-invoice-feedback subscription-invoice-feedback--error'
                                    : 'subscription-invoice-feedback'}>
                                    {invoiceMessage.text}
                                </p>
                            )}
                            {hasStripeAccount && (
                                <p className="subscription-invoice-hint">
                                    Loopt hier een Stripe-abonnement, dan komt het bedrag daar automatisch vandaan en wordt opslaan geweigerd.
                                </p>
                            )}
                            </div>
                        </div>
                    )}
                </div>

                {hasStripeAccount && (
                    <div className="subscription-actions">
                        <button onClick={handleManageSubscription} disabled={managing}>
                            {managing ? 'Even geduld…' : 'Abonnement beheren'}
                        </button>
                    </div>
                )}
            </div>

            <div className="profile-inner-container">
                <h2>Modules wijzigen</h2>
                {hasStripeAccount
                    ? <p>Kies welke modules je actief wilt. Wijzigingen gaan direct in en worden verrekend met je volgende factuur.</p>
                    : <p>Kies welke modules je actief wilt. Neem contact op met Deccos om je abonnement te wijzigen.</p>
                }

                    {MODULE_ORDER.map((slug) => {
                        const mod = MODULES[slug]
                        const freeForPartner = currentPartner?.freeIncludedModules?.includes(slug)
                        // Free-included partner-modules zijn altijd actief en mogen
                        // niet door de gebruiker worden uitgevinkt — gedrag matched
                        // de onboarding-flow (Step 4) waar diezelfde modules locked
                        // pre-checked staan.
                        const checked = freeForPartner || Boolean(draftModules[slug])
                        return (
                            <div key={slug} className="subscription-module-row">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => handleToggle(slug)}
                                        disabled={freeForPartner}
                                    />
                                    <span className="subscription-module-name">{mod.name}</span>
                                </label>
                                <button
                                    type="button"
                                    className="subscription-module-info-button"
                                    onClick={() => setInfoSlug(slug)}
                                    aria-label={`Meer informatie over ${mod.name}`}
                                >
                                    <InfoOutlinedIcon />
                                </button>
                                <span className="subscription-module-price">
                                    {freeForPartner ? 'Inbegrepen' : `€${mod.price}/mnd`}
                                </span>
                            </div>
                        )
                    })}

                    {moduleChanged && (
                        <div className="subscription-totals">
                            <div className="subscription-totals-row subscription-totals-row-new">
                                <span>Na wijziging</span>
                                <span>€{draftTotal}/mnd</span>
                            </div>
                        </div>
                    )}

                    {error && <p className="subscription-error">{error}</p>}

                    <div className="subscription-actions">
                        <button
                            onClick={handleApplyChanges}
                            disabled={checkoutDisabled}
                        >
                            {submitting ? 'Even geduld…' : 'Wijzigingen toepassen'}
                        </button>
                    </div>
            </div>

            {subscription?.pendingChanges && (
                <div className="subscription-banner">
                    <p>
                        Pending wijziging: modules{' '}
                        {subscription.pendingChanges.modulesToRemove?.join(', ')}{' '}
                        worden beëindigd op{' '}
                        {subscription.pendingChanges.effectiveDate?.toDate
                            ? subscription.pendingChanges.effectiveDate.toDate().toLocaleDateString('nl-NL')
                            : '...'}.
                    </p>
                </div>
            )}

            <Modal
                modalOpen={!!infoSlug}
                setModalOpen={() => setInfoSlug(null)}
                pageHeader={infoSlug ? MODULES[infoSlug].name : ''}
                hideConfirm={true}
                cancelLabel="Sluiten"
                content={infoSlug && (
                    <div className="subscription-module-info-modal">
                        <p className="subscription-module-info-tagline">
                            {MODULE_DESCRIPTIONS[infoSlug].tagline}
                        </p>
                        <p>{MODULE_DESCRIPTIONS[infoSlug].body}</p>
                        <h3>Wanneer past dit?</h3>
                        <p>{MODULE_DESCRIPTIONS[infoSlug].bestFor}</p>
                        <p className="subscription-module-info-price">
                            <strong>€{MODULES[infoSlug].price}</strong> per maand
                        </p>
                    </div>
                )}
            />

            <Modal
                modalOpen={contactModalOpen}
                setModalOpen={() => setContactModalOpen(false)}
                pageHeader="Abonnement wijzigen"
                hideConfirm={true}
                cancelLabel="Sluiten"
                content={(
                    <div className="subscription-module-info-modal">
                        <p>
                            Je betaalt per factuur. Neem contact op met Deccos via{' '}
                            <a href="mailto:info@deccos.nl">info@deccos.nl</a>{' '}
                            om je modules te wijzigen.
                        </p>
                    </div>
                )}
            />

        </div>
    )

}

export default Subscription
